import { EventType } from "@/analytics/analytics-events";
import { DeleteAnnotationEventProperties } from "@/analytics/annotation-events-properties";
import { AnnotationViewerVariants } from "@/components/ui/annotations/annotation-viewer/annotation-viewer-types";
import { ANNOTATION_ZINDEX_RANGE_MAP } from "@/components/ui/annotations/annotation-zindex-range-map";
import { MarkupAnnotationUI } from "@/components/ui/annotations/markup-annotation-ui";
import { useDeleteAnnotation } from "@/hooks/use-delete-annotation";
import { useAppSelector } from "@/store/store-hooks";
import { AnnotationShapeRenderer } from "@/tools/annotation-shapes/annotation-shape-renderer";
import {
  useAnnotationShapeStyle,
  useIsAnnotationShapesEnabled,
} from "@/tools/annotation-shapes/annotation-shapes-store";
import { annotationColorToCss } from "@/tools/annotation-shapes/annotation-colors";
import {
  convertIElementToThreePositionParsed,
  selectIElementWorldMatrix4,
} from "@/utils/transform-conversion-parsed";
import {
  MarkupPolygonIcon,
  MeasuresIcon,
  useBreakpointXXlUp,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  IElementGenericAnnotation,
  IElementType,
  isIElementExternalMarkup,
  isIElementImg360,
  isIElementMarkup,
  isIElementMarkupImagePoint,
  isIElementMarkupPolygon,
  isIElementMeasurePolygon,
} from "@faro-lotv/ielement-types";
import { equirectangular2DToSphere3D } from "@faro-lotv/lotv";
import {
  selectAncestor,
  selectChildDepthFirst,
} from "@faro-lotv/project-source";
import { Box } from "@mui/system";
import { animated, useSpringValue } from "@react-spring/web";
import { useThree } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import { CSSProperties, useCallback, useMemo, type JSX } from "react";
import { Material, Matrix4, Object3D, Vector3 } from "three";
import { useCopyToClipboard } from "../annotation-hooks";
import {
  useAnnotationSorterFadeChanged,
  useAnnotationSorterForceVisible,
  useAnnotationSorterWorldMatrix,
  useAnnotationVisibility,
} from "../annotation-sorter";
import { AnnotationVisibility } from "../annotation-utils";
import { AnnotationWrapper } from "../annotation-wrapper";
import { AlignAt } from "../annotations-types";
import { useHandleAnnotationViewerVariant } from "./use-handle-annotation-details-variant";

/** Size, in pixels, of the icon used to render polygon markups */
const MARKUP_ICON_SIZE = 20;

/** The ratio of the icon size to the icon hover size */
const MARKUP_ICON_HOVER_RATIO = 1.2;

/** The minimum width of the annotation (in px)*/
const ANNOTATION_MIN_WIDTH = 200;
/** The ratio of the annotation viewer width to the canvas width */
const ANNOTATION_WIDTH_RATIO = 0.3;
/** The ratio of the annotation viewer width to the canvas width on big screens */
const ANNOTATION_WIDTH_RATIO_BIG_SCREENS = 0.25;
/** The ratio of the annotation viewer height to the canvas height */
const ANNOTATION_HEIGHT_RATIO = 0.85;

type MarkupAnnotationRendererProps = {
  /** The annotation to render */
  annotation: IElementGenericAnnotation;

  /** The world offset applied to the pano associated to these markups */
  worldTransform: Matrix4;

  /** Whether depth testing should be used to render the annotation */
  depthTest?: Material["depthTest"];

  /** The render order to use for the annotation */
  renderOrder?: Object3D["renderOrder"];

  /** Indicates if the annotation is an external annotation */
  isExternalAnnotation?: boolean;
};

/** @returns handles the rendering of annotation of type markup */
export function MarkupAnnotationRenderer({
  annotation,
  worldTransform,
  depthTest,
  renderOrder,
  isExternalAnnotation,
}: MarkupAnnotationRendererProps): JSX.Element | null {
  const canvas = useThree(({ gl }) => gl.domElement);

  const markup = useAppSelector(
    selectChildDepthFirst(
      annotation,
      isExternalAnnotation ? isIElementExternalMarkup : isIElementMarkup,
    ),
  );

  assert(markup !== undefined, "markup is undefined");
  assert(
    isIElementExternalMarkup(markup) || isIElementMarkup(markup),
    "The iElement is not a markup",
  );

  const annotationWorldTransform = useAppSelector(
    selectIElementWorldMatrix4(annotation.id),
    isEqual,
  );

  const anchorPoint = useAnnotationAnchorPoint(annotation);

  const transform = useMemo(
    () =>
      new Matrix4().multiplyMatrices(worldTransform, annotationWorldTransform),
    [annotationWorldTransform, worldTransform],
  );

  const anchorPointWorld = useMemo(
    () => anchorPoint.clone().applyMatrix4(transform),
    [anchorPoint, transform],
  );

  const visibility = useAnnotationVisibility(annotation.id);

  const {
    AnnotationViewerVariant,
    onAnnotationViewerHovered,
    onAnnotationViewerExpanded,
    onAnnotationViewerClosed,
    onAnnotationViewerClicked,
  } = useHandleAnnotationViewerVariant(
    markup.id,
    visibility === AnnotationVisibility.Minified,
    visibility === AnnotationVisibility.NotVisible,
  );

  const { uiStyle, alignAt } = useAnnotationStyle(
    annotation,
    AnnotationViewerVariant,
  );

  useAnnotationSorterWorldMatrix(annotation.id, transform);
  useAnnotationSorterForceVisible(
    annotation.id,
    AnnotationViewerVariant === "full",
  );

  // Using a spring bypasses reactivity for faster, high-frequency updates
  const fadeSpring = useSpringValue(0);
  const onAnnotationFadeChanged = useCallback(
    (fade: number) => {
      fadeSpring.set(fade);
    },
    [fadeSpring],
  );

  useAnnotationSorterFadeChanged(annotation.id, onAnnotationFadeChanged);

  const hasShapes = useIsAnnotationShapesEnabled();
  const shapeStyle = useAnnotationShapeStyle(markup.id);
  const pinColor =
    hasShapes && shapeStyle
      ? annotationColorToCss(shapeStyle.color)
      : undefined;

  // Optional icon to render next to the UI. Used for single point annotations
  const icon = useMemo(() => {
    if (
      AnnotationViewerVariant === "collapsed" ||
      AnnotationViewerVariant === "hidden"
    ) {
      return (
        <animated.div style={{ opacity: fadeSpring }}>
          <CollapsedAnnotationIcon annotation={annotation} color={pinColor} />
        </animated.div>
      );
    }

    // This is the icon version used when the annotation is expanded and the box is shown
    if (
      isIElementMarkupPolygon(annotation) ||
      isIElementMarkupImagePoint(annotation)
    ) {
      return (
        <MarkupPolygonIcon
          sx={{
            fontSize: `${MARKUP_ICON_SIZE * MARKUP_ICON_HOVER_RATIO}px`,
            color: pinColor,
          }}
        />
      );
    }
  }, [AnnotationViewerVariant, annotation, fadeSpring, pinColor]);

  const copyToClipboard = useCopyToClipboard(annotation);

  const { removeAnnotation } = useDeleteAnnotation(markup);

  const isScreenXXLUp = useBreakpointXXlUp();

  const annotationWidth = useMemo(() => {
    // If the screen is XXL or larger, the annotation width is smaller
    if (isScreenXXLUp) {
      return Math.floor(
        canvas.clientWidth * ANNOTATION_WIDTH_RATIO_BIG_SCREENS,
      );
    }
    return Math.floor(canvas.clientWidth * ANNOTATION_WIDTH_RATIO);
  }, [canvas.clientWidth, isScreenXXLUp]);

  if (AnnotationViewerVariant === "hidden") {
    return null;
  }

  return (
    <>
      {hasShapes && isIElementMarkupPolygon(annotation) && (
        <AnnotationShapeRenderer
          markupId={markup.id}
          points={annotation.points}
          transform={transform}
          variant={AnnotationViewerVariant}
        />
      )}
      <AnnotationWrapper
        iElement={annotation}
        worldTransform={worldTransform}
        onHoverChange={onAnnotationViewerHovered}
        popOverAlwaysVisible
        alignAt={alignAt}
        anchorPointWorld={anchorPointWorld}
        eps={-1}
        zIndexRange={ANNOTATION_ZINDEX_RANGE_MAP[AnnotationViewerVariant]}
        isCollapsed={AnnotationViewerVariant === "collapsed"}
        depthTest={depthTest}
        renderOrder={renderOrder}
        style={uiStyle}
      >
        <MarkupAnnotationUI
          markup={markup}
          AnnotationViewerVariant={AnnotationViewerVariant}
          onAnnotationViewerExpanded={onAnnotationViewerExpanded}
          onAnnotationViewerClosed={onAnnotationViewerClosed}
          onAnnotationViewerClicked={onAnnotationViewerClicked}
          icon={icon}
          // Use the biggest icon size so that it has space to expand on hover
          iconSize={MARKUP_ICON_SIZE * MARKUP_ICON_HOVER_RATIO}
          annotationSize={{
            minWidth: ANNOTATION_MIN_WIDTH,
            width: annotationWidth,
            height: Math.floor(canvas.clientHeight * ANNOTATION_HEIGHT_RATIO),
          }}
          onCopyToClipboard={copyToClipboard}
          onDelete={() => {
            Analytics.track<DeleteAnnotationEventProperties>(
              EventType.deleteAnnotation,
              { via: "3d scene" },
            );
            removeAnnotation();
          }}
        />
      </AnnotationWrapper>
    </>
  );
}

type CollapsedAnnotationIconProps = {
  /** The IElement of the annotation. It's used to choose which icon to render. */
  annotation: IElementGenericAnnotation;

  /** Optional color of the pin, from the shape style */
  color?: string;
};

/** @returns The icon to render for a collapsed annotation */
function CollapsedAnnotationIcon({
  annotation,
  color,
}: CollapsedAnnotationIconProps): JSX.Element {
  if (isIElementMeasurePolygon(annotation)) {
    return (
      <>
        {/* The 'Box' below is needed so that the MeasureIcon can be rendered inside a
         * div and therefore appear in screenshots. */}
        <Box
          component="div"
          sx={{ transform: `translate(0px, -${MARKUP_ICON_SIZE}px)` }}
        >
          <MeasuresIcon
            sx={{
              fontSize: `${MARKUP_ICON_SIZE}px`,
              verticalAlign: "bottom",
              ":hover": {
                fontSize: `${MARKUP_ICON_SIZE * MARKUP_ICON_HOVER_RATIO}px`,
              },
            }}
          />
        </Box>
      </>
    );
  }

  return (
    <MarkupPolygonIcon
      sx={{
        fontSize: `${MARKUP_ICON_SIZE}px`,
        color,
        verticalAlign: "bottom",
        ":hover": {
          fontSize: `${MARKUP_ICON_SIZE * MARKUP_ICON_HOVER_RATIO}px`,
        },
      }}
    />
  );
}

type UseAnnotationStyleReturn = {
  /** Additional styles needed to customize the label */
  uiStyle: CSSProperties;

  /** Position of the label relative to the object, in case the anchor point is not given */
  alignAt: AlignAt;
};

/**
 * The anchor point is an additional local offset of an annotation IElement, at which the "main position" should be considered.
 * For example to determine where a collapsed annotation should be shown, or where markup UI should be anchored to.
 *
 * @param annotation IElement of the annotation to render
 * @returns the anchor point of the annotation
 */
function useAnnotationAnchorPoint(
  annotation: IElementGenericAnnotation,
): Vector3 {
  const parentPano = useAppSelector(
    selectAncestor(annotation, isIElementImg360),
    isEqual,
  );

  return useMemo(() => {
    switch (annotation.type) {
      case IElementType.markupImagePoint: {
        if (!parentPano) {
          return new Vector3();
        }

        return equirectangular2DToSphere3D(
          annotation.pixelPosition,
          parentPano.pixelWidth,
          parentPano.pixelHeight,
        );
      }
      case IElementType.markupPolygon: {
        // The MarkupPolygon will have the same position for the anchor point as the 3d object
        return convertIElementToThreePositionParsed(annotation.points[0]);
      }
      case IElementType.measurePolygon: {
        const points = annotation.points.map(
          convertIElementToThreePositionParsed,
        );
        // Place the annotation at the "left-most" point
        return points.reduce(
          (prev, next) => {
            if (next.x < prev.x) prev.copy(next);
            return prev;
          },
          new Vector3(
            Number.POSITIVE_INFINITY,
            Number.POSITIVE_INFINITY,
            Number.POSITIVE_INFINITY,
          ),
        );
      }
      default:
        return new Vector3();
    }
  }, [annotation, parentPano]);
}

/**
 * @param annotation IElement of the annotation to render
 * @param variant current variant of the annotation
 * @returns the style to apply to the annotation's ui and how the annotation should be aligned
 */
function useAnnotationStyle(
  annotation: IElementGenericAnnotation,
  variant: AnnotationViewerVariants,
): UseAnnotationStyleReturn {
  let uiStyle: CSSProperties = {};
  let alignAt: AlignAt = AlignAt.center;

  switch (annotation.type) {
    case IElementType.markupImagePoint:
    case IElementType.markupPolygon: {
      alignAt = AlignAt.anchorPoint;

      // Move the UI to the top so that the icon bottom-left corner points
      // to the markup polygon position
      uiStyle = {
        transform: `translate(0px, ${-MARKUP_ICON_SIZE}px)`,
      };
      break;
    }
    case IElementType.measurePolygon: {
      alignAt = AlignAt.anchorPoint;
      break;
    }
    case IElementType.model3d: {
      alignAt =
        variant === "full"
          ? AlignAt.boundingBoxTopRight
          : AlignAt.boundingBoxTopLeft;

      uiStyle = {
        transform:
          variant === "full" ? "translate(8px, 0px)" : "translate(8px, -10px)",
      };
      break;
    }
  }

  return { uiStyle, alignAt };
}
